import { createTheme, ThemeOptions } from '@mui/material/styles';
import { COLORS, getThemeColors } from '@/constants/colors';

// Color utility functions
const lightenColor = (color: string, amount: number): string => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const newR = Math.min(255, Math.floor(r + (255 - r) * amount));
  const newG = Math.min(255, Math.floor(g + (255 - g) * amount));
  const newB = Math.min(255, Math.floor(b + (255 - b) * amount));
  
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

const darkenColor = (color: string, amount: number): string => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const newR = Math.max(0, Math.floor(r * (1 - amount)));
  const newG = Math.max(0, Math.floor(g * (1 - amount)));
  const newB = Math.max(0, Math.floor(b * (1 - amount)));
  
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

interface ExtendedThemeOptions extends ThemeOptions {
  palette: {
    mode: 'light' | 'dark';
    primary: {
      main: string;
      light: string;
      dark: string;
    };
    secondary: {
      main: string;
      light: string;
      dark: string;
    };
    background: {
      default: string;
      paper: string;
    };
    text: {
      primary: string;
      secondary: string;
    };
  };
}

const getTheme = (mode: 'light' | 'dark', customColors?: { primary: string; secondary: string }) => {
  const themeColors = getThemeColors(mode);
  
  const themeOptions: ExtendedThemeOptions = {
    palette: {
      mode,
      primary: {
        main: customColors?.primary || COLORS.PRIMARY.MAIN,
        light: mode === 'light' 
          ? (customColors?.primary ? lightenColor(customColors.primary, 0.3) : COLORS.PRIMARY.LIGHT)
          : (customColors?.primary ? lightenColor(customColors.primary, 0.5) : lightenColor(COLORS.PRIMARY.MAIN, 0.5)),
        dark: mode === 'light' 
          ? (customColors?.primary ? darkenColor(customColors.primary, 0.3) : COLORS.PRIMARY.DARK)
          : (customColors?.primary ? darkenColor(customColors.primary, 0.5) : darkenColor(COLORS.PRIMARY.MAIN, 0.5)),
      },
      secondary: {
        main: customColors?.secondary || COLORS.SECONDARY.MAIN,
        light: mode === 'light' 
          ? (customColors?.secondary ? lightenColor(customColors.secondary, 0.3) : COLORS.SECONDARY.LIGHT)
          : (customColors?.secondary ? lightenColor(customColors.secondary, 0.5) : lightenColor(COLORS.SECONDARY.MAIN, 0.5)),
        dark: mode === 'light' 
          ? (customColors?.secondary ? darkenColor(customColors.secondary, 0.3) : COLORS.SECONDARY.DARK)
          : (customColors?.secondary ? darkenColor(customColors.secondary, 0.5) : darkenColor(COLORS.SECONDARY.MAIN, 0.5)),
      },
      background: {
        default: themeColors.background.DEFAULT,
        paper: themeColors.background.PAPER,
      },
      text: {
        primary: themeColors.text.PRIMARY,
        secondary: themeColors.text.SECONDARY,
      },
    },
    typography: {
      fontFamily: '"Montserrat", "Segoe UI", "Roboto", "Ubuntu", "Droid Sans", sans-serif',
      h1: {
        fontSize: '2rem',
        fontWeight: 600,
        lineHeight: 1.23,
        color: mode === 'light' ? '#262626' : '#ffffff',
      },
      h2: {
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.25,
        color: mode === 'light' ? '#262626' : '#ffffff',
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.35,
        color: mode === 'light' ? '#262626' : '#ffffff',
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
        color: mode === 'light' ? '#262626' : '#ffffff',
      },
      h5: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5,
        color: mode === 'light' ? '#262626' : '#ffffff',
      },
      h6: {
        fontSize: '0.875rem',
        fontWeight: 600,
        lineHeight: 1.6,
        color: mode === 'light' ? '#262626' : '#ffffff',
      },
      body1: {
        fontSize: '0.875rem',
        lineHeight: 1.5715,
        color: mode === 'light' ? '#262626' : '#ffffff',
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5715,
        color: mode === 'light' ? '#8c8c8c' : '#bfbfbf',
      },
    },
    components: {
      // Global CSS classes for consistent hover effects
      MuiCssBaseline: {
        styleOverrides: {
          '*, *::before, *::after': {
            boxSizing: 'border-box',
          },
          // Global hover shadow utility classes
          '.hover-shadow': {
            transition: 'all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)',
            '&:hover': {
              boxShadow: mode === 'light' 
                ? 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px !important'
                : 'rgba(255, 255, 255, 0.05) 0px 6px 24px 0px, rgba(255, 255, 255, 0.08) 0px 0px 0px 1px !important',
            },
          },
          '.hover-shadow-subtle': {
            transition: 'all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)',
            '&:hover': {
              boxShadow: mode === 'light' 
                ? 'rgba(0, 0, 0, 0.03) 0px 4px 16px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px !important'
                : 'rgba(255, 255, 255, 0.03) 0px 4px 16px 0px, rgba(255, 255, 255, 0.06) 0px 0px 0px 1px !important',
            },
          },
          '.hover-shadow-strong': {
            transition: 'all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)',
            '&:hover': {
              boxShadow: mode === 'light' 
                ? 'rgba(0, 0, 0, 0.08) 0px 8px 32px 0px, rgba(0, 0, 0, 0.12) 0px 0px 0px 1px !important'
                : 'rgba(255, 255, 255, 0.08) 0px 8px 32px 0px, rgba(255, 255, 255, 0.12) 0px 0px 0px 1px !important',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 6,
            fontWeight: 400,
            fontSize: '0.875rem',
            padding: '4px 15px',
            height: 32,
            lineHeight: 1.5715,
            border: '1px solid transparent',
            transition: 'all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)',
            '&:focus': {
              outline: 'none',
              boxShadow: 'none',
            },
            '&:focus-visible': {
              outline: 'none',
              boxShadow: `0 0 0 2px ${mode === 'light' ? 'rgba(24, 144, 255, 0.2)' : 'rgba(144, 202, 249, 0.2)'}`,
            },
          },
          contained: {
            boxShadow: 'none',
            border: '1px solid transparent',
            '&:hover': {
              boxShadow: 'none',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: 'none',
            },
          },
          outlined: {
            borderColor: mode === 'light' ? '#d9d9d9' : '#434343',
            backgroundColor: 'transparent',
            '&:hover': {
              borderColor: mode === 'light' ? '#40a9ff' : '#40a9ff',
              color: mode === 'light' ? '#40a9ff' : '#40a9ff',
            },
          },
          sizeLarge: {
            height: 40,
            padding: '6.4px 15px',
            fontSize: '1rem',
          },
          sizeSmall: {
            height: 24,
            padding: '0px 7px',
            fontSize: '0.875rem',
          },
        },
      },
      MuiTextField: {
  defaultProps: {
    size: 'small',
  },
  styleOverrides: {
    root: {
        '& .MuiFormLabel-asterisk': {
        color: 'red',
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: 6,
        fontSize: '0.875rem',
        backgroundColor: 'transparent',
        transition: 'all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)',
        '& fieldset': {
          borderColor: mode === 'light' ? '#d9d9d9' : '#434343',
        },
        '&:hover fieldset': {
          borderColor: '#40a9ff',
        },
        '&.Mui-focused fieldset': {
          borderWidth: 1,
          borderColor: '#1890ff',
          boxShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)',
        },
        '& input': {
          backgroundColor: 'transparent !important',
          '&:focus': {
            outline: 'none',
          },
          '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
            WebkitTextFillColor: mode === 'light' ? '#262626' : '#ffffff',
            transition: 'background-color 5000s ease-in-out 0s',
          },
        },
        '& textarea': {
          backgroundColor: 'transparent !important',
          resize: 'vertical',
          '&:focus': {
            outline: 'none',
          },
        },
      },
    },
  },
},

      // MuiTextField: {
      //   styleOverrides: {
      //     root: {
      //       '& .MuiOutlinedInput-root': {
      //         borderRadius: 6,
      //         fontSize: '0.875rem',
      //         backgroundColor: 'transparent',
      //         transition: 'all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)',
      //         '& fieldset': {
      //           borderColor: mode === 'light' ? '#d9d9d9' : '#434343',
      //         },
      //         '&:hover fieldset': {
      //           borderColor: mode === 'light' ? '#40a9ff' : '#40a9ff',
      //         },
      //         '&.Mui-focused fieldset': {
      //           borderWidth: 1,
      //           borderColor: mode === 'light' ? '#1890ff' : '#1890ff',
      //           boxShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)',
      //         },
      //         '& input': {
      //           padding: '7px 11px',
      //           height: '1.5715em',
      //           backgroundColor: 'transparent !important',
      //           '&:focus': {
      //             outline: 'none',
      //           },
      //           '&:-webkit-autofill': {
      //             WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
      //             WebkitTextFillColor: mode === 'light' ? '#262626' : '#ffffff',
      //             transition: 'background-color 5000s ease-in-out 0s',
      //           },
      //           '&:-webkit-autofill:hover': {
      //             WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
      //           },
      //           '&:-webkit-autofill:focus': {
      //             WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
      //           },
      //           '&:-webkit-autofill:active': {
      //             WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
      //           },
      //         },
      //         '& textarea': {
      //           padding: '7px 11px',
      //           backgroundColor: 'transparent !important',
      //           resize: 'vertical',
      //           '&:focus': {
      //             outline: 'none',
      //           },
      //         },
      //       },
      //     },
      //   },
      // },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: 'none !important',
            border: `1px solid ${mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
            transition: 'all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)',
            '&:hover': {
              borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
              boxShadow: mode === 'light' 
                ? 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px !important'
                : 'rgba(255, 255, 255, 0.05) 0px 6px 24px 0px, rgba(255, 255, 255, 0.08) 0px 0px 0px 1px !important',
            },
            // '&.MuiPaper-elevation0': {
            //   boxShadow: 'none !important',
            // },
            // '&.MuiPaper-elevation1': {
            //   boxShadow: 'none !important',
            // },
            // '&.MuiPaper-elevation2': {
            //   boxShadow: 'none !important',
            // },
            // '&.MuiPaper-elevation3': {
            //   boxShadow: 'none !important',
            // },
            // '&.MuiPaper-elevation4': {
            //   boxShadow: 'none !important',
            // },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundImage: 'none',
            boxShadow: 'none !important',
            border: `1px solid ${mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
            // Add hover-shadow class to Papers that should have hover effect
            '&.hover-shadow': {
              transition: 'all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)',
              '&:hover': {
                borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
                boxShadow: mode === 'light' 
                  ? 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px !important'
                  : 'rgba(255, 255, 255, 0.05) 0px 6px 24px 0px, rgba(255, 255, 255, 0.08) 0px 0px 0px 1px !important',
              },
            },
          },
          elevation0: {
            boxShadow: 'none !important',
          },
          elevation1: {
            boxShadow: 'none !important',
          },
          elevation2: {
            boxShadow: 'none !important',
          },
          elevation3: {
            boxShadow: 'none !important',
          },
          elevation4: {
            boxShadow: 'none !important',
          },
          elevation8: {
            boxShadow: 'none !important',
          },
          elevation16: {
            boxShadow: 'none !important',
          },
          elevation24: {
            boxShadow: 'none !important',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
            boxShadow: 'none',
            border: 'none',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderBottom: `1px solid ${mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
            backgroundColor: mode === 'light' ? '#ffffff' : '#1f1f1f',
            color: mode === 'light' ? '#262626' : '#ffffff',
            '& .MuiIconButton-root': {
              color: mode === 'light' ? '#262626' : '#ffffff',
            },
            '& .MuiTypography-root': {
              color: mode === 'light' ? '#262626' : '#ffffff',
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            fontSize: '0.75rem',
            fontWeight: 500,
            height: 24,
            '& .MuiChip-label': {
              paddingLeft: 8,
              paddingRight: 8,
              paddingTop: 2,
              paddingBottom: 2,
            },
            '& .MuiChip-deleteIcon': {
              color: 'inherit',
              '&:hover': {
                color: 'inherit',
              },
            },
          },
          colorPrimary: {
            backgroundColor: mode === 'light' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(144, 202, 249, 0.08)',
            color: mode === 'light' ? 'rgb(13, 60, 97)' : 'rgb(144, 202, 249)',
            border: `1px solid ${mode === 'light' ? 'rgba(25, 118, 210, 0.12)' : 'rgba(144, 202, 249, 0.12)'}`,
          },
          colorSecondary: {
            backgroundColor: mode === 'light' ? 'rgba(156, 39, 176, 0.08)' : 'rgba(206, 147, 216, 0.08)',
            color: mode === 'light' ? 'rgb(74, 20, 140)' : 'rgb(206, 147, 216)',
            border: `1px solid ${mode === 'light' ? 'rgba(156, 39, 176, 0.12)' : 'rgba(206, 147, 216, 0.12)'}`,
          },
          colorInfo: {
            backgroundColor: mode === 'light' ? 'rgba(2, 136, 209, 0.08)' : 'rgba(79, 195, 247, 0.08)',
            color: mode === 'light' ? 'rgb(1, 67, 97)' : 'rgb(79, 195, 247)',
            border: `1px solid ${mode === 'light' ? 'rgba(2, 136, 209, 0.12)' : 'rgba(79, 195, 247, 0.12)'}`,
          },
          colorSuccess: {
            backgroundColor: mode === 'light' ? 'rgba(46, 125, 50, 0.08)' : 'rgba(129, 199, 132, 0.08)',
            color: mode === 'light' ? 'rgb(25, 67, 27)' : 'rgb(129, 199, 132)',
            border: `1px solid ${mode === 'light' ? 'rgba(46, 125, 50, 0.12)' : 'rgba(129, 199, 132, 0.12)'}`,
          },
          colorWarning: {
            backgroundColor: mode === 'light' ? 'rgba(237, 108, 2, 0.08)' : 'rgba(255, 183, 77, 0.08)',
            color: mode === 'light' ? 'rgb(102, 46, 0)' : 'rgb(255, 183, 77)',
            border: `1px solid ${mode === 'light' ? 'rgba(237, 108, 2, 0.12)' : 'rgba(255, 183, 77, 0.12)'}`,
          },
          colorError: {
            backgroundColor: mode === 'light' ? 'rgba(211, 47, 47, 0.08)' : 'rgba(239, 154, 154, 0.08)',
            color: mode === 'light' ? 'rgb(95, 20, 20)' : 'rgb(239, 154, 154)',
            border: `1px solid ${mode === 'light' ? 'rgba(211, 47, 47, 0.12)' : 'rgba(239, 154, 154, 0.12)'}`,
          },
        },
      },
      MuiSvgIcon: {
        defaultProps: {
          fontSize: 'small',
        },
        styleOverrides: {
          root: {
            fontSize: '1rem',
          },
          fontSizeSmall: {
            fontSize: '1rem',
          },
          fontSizeMedium: {
            fontSize: '1.25rem',
          },
          fontSizeLarge: {
            fontSize: '1.5rem',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&:focus': {
              outline: 'none',
              backgroundColor: 'transparent',
            },
            '&:focus-visible': {
              outline: 'none',
              backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&:focus-within': {
              outline: 'none',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              '&:focus': {
                outline: 'none',
              },
            },
          },
        },
      },
    },
    shape: {
      borderRadius: 6,
    },
  };

  return createTheme(themeOptions);
};

export const createAppTheme = getTheme;
export default getTheme;